import api from './api';

// Marketplace stage 1 — a manufacturer is a hardware seller. Boards in the
// catalog belong to one, manufacturer_admin users belong to one, and (from
// stage 2) payouts go to one. Simkura is itself a row.
export interface Manufacturer {
  id: number;
  name: string;
  slug: string;
  support_email: string | null;
  website: string | null;
  active: boolean;
  notes: string | null;
  // Public profile shown on the catalog page (provider-editable).
  tagline: string | null;
  about: string | null;
  /** Whether this provider extends terms (net billing) to buyers at checkout. */
  offers_terms: boolean;
  has_logo: boolean;
  logo_content_type: string | null;
  logo_updated_at: string | null;
  board_count: number;
  user_count: number;
  // Devices built on this manufacturer's boards (through devices.board_id).
  device_count: number;
  deployed_device_count: number;
  software_provider_count: number;
  created_at: string;
  updated_at: string;
}

// One of the manufacturer's units in the field — a narrow projection: which
// board, whether deployed, and which software provider (company) it belongs to.
export interface ManufacturerDevice {
  id: string;
  device_id: string;
  name: string | null;
  deployed: boolean;
  deployed_at: string | null;
  last_seen: string | null;
  hardware_version: string | null;
  created_at: string;
  status: 'online' | 'offline' | 'unknown';
  board_id: number;
  board: string;
  board_display_name: string | null;
  company_id: string | number | null;
  company_name: string | null;
}

export interface SoftwareProviderBreakdown {
  company_id: string | number;
  company_name: string;
  device_count: number;
  deployed_count: number;
}

export interface ManufacturerDevicesResponse {
  devices: ManufacturerDevice[];
  total: number;
  software_providers: SoftwareProviderBreakdown[];
  limit: number;
  offset: number;
}

export async function getManufacturerDevices(
  id: number,
  options: { deployed?: boolean; companyId?: string | number; limit?: number; offset?: number } = {}
): Promise<ManufacturerDevicesResponse> {
  const params = new URLSearchParams();
  if (options.deployed !== undefined) params.set('deployed', String(options.deployed));
  if (options.companyId) params.set('companyId', String(options.companyId));
  if (options.limit) params.set('limit', String(options.limit));
  if (options.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  const response = await api.get<ManufacturerDevicesResponse>(`/manufacturers/${id}/devices${qs ? `?${qs}` : ''}`);
  return response.data;
}

export type ManufacturerInput = Partial<
  Omit<Manufacturer, 'id' | 'board_count' | 'user_count' | 'created_at' | 'updated_at'>
>;

export async function getManufacturers(options: { active?: boolean } = {}): Promise<Manufacturer[]> {
  const params = options.active ? '?active=true' : '';
  const response = await api.get<{ manufacturers: Manufacturer[] }>(`/manufacturers${params}`);
  return response.data.manufacturers;
}

export async function getManufacturer(id: number): Promise<Manufacturer> {
  const response = await api.get<Manufacturer>(`/manufacturers/${id}`);
  return response.data;
}

export async function createManufacturer(data: ManufacturerInput): Promise<Manufacturer> {
  const response = await api.post<Manufacturer>('/manufacturers', data);
  return response.data;
}

export async function updateManufacturer(id: number, data: ManufacturerInput): Promise<Manufacturer> {
  const response = await api.put<Manufacturer>(`/manufacturers/${id}`, data);
  return response.data;
}

export const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp';

const apiBase = () => (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');

/** Authenticated logo preview (dashboard); fetch it with AuthImage. */
export function logoPreviewUrl(id: number): string {
  return `${apiBase()}/manufacturers/${id}/logo`;
}

/** Anonymous logo URL for the public catalog (active providers only). */
export function providerLogoUrl(slug: string, version?: string | null): string {
  return `${apiBase()}/files/provider-logo/${slug}${version ? `?v=${encodeURIComponent(version)}` : ''}`;
}

export async function uploadLogo(id: number, file: File): Promise<Manufacturer> {
  const form = new FormData();
  form.append('file', file, file.name);
  const response = await api.put<Manufacturer>(`/manufacturers/${id}/logo`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function deleteLogo(id: number): Promise<Manufacturer> {
  const response = await api.delete<Manufacturer>(`/manufacturers/${id}/logo`);
  return response.data;
}

// ── Payouts (Stripe Connect Express) ────────────────────────────────────────

export interface PayoutStatus {
  configured: boolean;        // Stripe enabled on this environment
  onboarded: boolean;         // a connected account exists
  payoutsEnabled: boolean;    // Stripe cleared the account to receive payouts
  detailsSubmitted: boolean;  // onboarding form finished (may await review)
  accountId?: string;
}

export async function getPayoutStatus(id: number): Promise<PayoutStatus> {
  const response = await api.get<PayoutStatus>(`/manufacturers/${id}/payouts`);
  return response.data;
}

/** Start (or resume) Stripe onboarding; navigate to the returned URL. */
export async function startPayoutOnboarding(id: number): Promise<{ url: string }> {
  const response = await api.post<{ url: string }>(`/manufacturers/${id}/payouts/onboard`);
  return response.data;
}

/** Stripe Express dashboard login link (once onboarded). */
export async function getPayoutLoginLink(id: number): Promise<{ url: string }> {
  const response = await api.post<{ url: string }>(`/manufacturers/${id}/payouts/login-link`);
  return response.data;
}

// Recurring service revenue: the provider's monthly share of buyers' service
// statements (gross minus the platform cut), with the transfer state.
export interface ServiceShare {
  id: number;
  statement_id: number;
  period_start: string;
  currency: string;
  statement_status: 'invoiced' | 'paid' | 'void';
  company_name: string;
  gross_cents: number;
  platform_fee_cents: number;
  net_cents: number;
  transfer_status: 'pending' | 'not_onboarded' | 'transferred' | 'failed';
  stripe_transfer_id: string | null;
  transferred_at: string | null;
}

export async function getServiceShares(id: number): Promise<{ shares: ServiceShare[] }> {
  const response = await api.get(`/manufacturers/${id}/service-shares`);
  return response.data;
}

export async function deleteManufacturer(id: number): Promise<{ success: boolean }> {
  const response = await api.delete<{ success: boolean }>(`/manufacturers/${id}`);
  return response.data;
}
